/**
 * OnboardingContext
 * Manages onboarding state - ONLY shows on first signup
 * Thin wrapper around onboarding service
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as onboardingService from '../services/onboardingService';
import { useAuth } from '../../auth/context/AuthContext';
import logger from '@/shared/utils/core/logger';
import { trackOnboardingStarted, trackOnboardingError } from '@/shared/utils/analytics/analytics';


const OnboardingContext = createContext(null);

export function OnboardingProvider({ children, user }) {
  const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [onboardingData, setOnboardingData] = useState(null);
  const [onboardingStartTime, setOnboardingStartTime] = useState(null);

  // Get authInitialized from AuthContext to ensure we don't check too early
  const { authInitialized } = useAuth();

  const hasChecked = useRef(false);
  const previousUserId = useRef(null);
  const checkInProgress = useRef(false);

  // Check onboarding status when user changes
  // IMPORTANT: Only check AFTER auth is fully initialized to avoid race conditions
  useEffect(() => {
    // Don't check until auth is fully initialized
    if (!authInitialized) {
      logger.debug('⏳ [OnboardingContext] Waiting for auth to initialize...');
      return;
    }

    if (user && user.uid !== previousUserId.current) {
      logger.debug('👤 [OnboardingContext] User logged in, checking onboarding status...');
      previousUserId.current = user.uid;
      hasChecked.current = false;
      checkOnboardingStatus();
    } else if (!user) {
      // User logged out - reset state
      logger.debug('👋 [OnboardingContext] User logged out, resetting state');
      resetState();
    }
  }, [user?.uid, authInitialized]);

  const resetState = () => {
    setIsChecking(false);
    setShouldShowOnboarding(false);
    setOnboardingData(null);
    hasChecked.current = false;
    previousUserId.current = null;
    checkInProgress.current = false;
  };

  const checkOnboardingStatus = useCallback(async () => {
    // Prevent duplicate checks
    if (!user || hasChecked.current || checkInProgress.current) {
      logger.debug('[OnboardingContext] Skipping check - already in progress or completed');
      return;
    }

    try {
      setIsChecking(true);
      hasChecked.current = true;
      checkInProgress.current = true;

      // Check status using service
      const status = await onboardingService.checkOnboardingStatus(user);

      // If user needs onboarding, mark as shown BEFORE updating state
      // This prevents race conditions if user refreshes
      if (status.needsOnboarding) {
        await onboardingService.markOnboardingShown(user);

        // Track onboarding started and record start time
        const authMethod = status.profileData?.provider || 'unknown';
        trackOnboardingStarted({
          authMethod,
          isNewUser: true
        });
        setOnboardingStartTime(Date.now());
      }

      setShouldShowOnboarding(status.needsOnboarding);
      setOnboardingData(status.profileData);
    } catch (error) {
      logger.error('[OnboardingContext] Error checking status:', error);
      // On error, don't show onboarding to avoid blocking user
      setShouldShowOnboarding(false);
      hasChecked.current = true; // Don't retry on error
    } finally {
      setIsChecking(false);
      checkInProgress.current = false;
    }
  }, [user]);

  const markOnboardingComplete = useCallback(async (options = {}) => {
    if (!user) {
      logger.warn('[OnboardingContext] Cannot mark complete - no user');
      setShouldShowOnboarding(false);
      return;
    }

    // Hide immediately for better UX
    setShouldShowOnboarding(false);

    try {
      // Calculate total time spent in onboarding
      const totalTime = onboardingStartTime ? Date.now() - onboardingStartTime : 0;
      const authMethod = onboardingData?.provider || 'unknown';

      await onboardingService.markOnboardingComplete(user, {
        authMethod,
        totalTime,
        stepsCompleted: options.stepsCompleted || 4,
        hasBirthDate: options.hasBirthDate || false,
        hasBirthTime: options.hasBirthTime || false,
        hasBirthPlace: options.hasBirthPlace || false
      });
    } catch (error) {
      logger.error('[OnboardingContext] Error marking complete:', error);
      trackOnboardingError({
        errorType: 'context_mark_complete_failed',
        errorMessage: error.message,
        step: 'completion'
      });
      // Already hidden above - user can continue using app
    }
  }, [user, onboardingStartTime, onboardingData]);

  const skipOnboarding = useCallback(async (options = {}) => {
    if (!user) {
      logger.warn('[OnboardingContext] Cannot skip - no user');
      setShouldShowOnboarding(false);
      return;
    }

    // Hide immediately for better UX
    setShouldShowOnboarding(false);

    try {
      // Calculate time before skip
      const timeBeforeSkip = onboardingStartTime ? Date.now() - onboardingStartTime : 0;
      const authMethod = onboardingData?.provider || 'unknown';

      await onboardingService.markOnboardingSkipped(user, {
        skippedAtStep: options.skippedAtStep || 'welcome',
        timeBeforeSkip,
        authMethod
      });
    } catch (error) {
      logger.error('[OnboardingContext] Error saving skip:', error);
      trackOnboardingError({
        errorType: 'context_mark_skipped_failed',
        errorMessage: error.message,
        step: options.skippedAtStep || 'unknown'
      });
      // Already hidden above - user can continue using app
    }
  }, [user, onboardingStartTime, onboardingData]);

  const resetOnboarding = useCallback(async () => {
    if (!user) {
      logger.warn('[OnboardingContext] Cannot reset - no user');
      return;
    }

    try {
      await onboardingService.resetOnboarding(user);

      // Reset local state
      hasChecked.current = false;
      setShouldShowOnboarding(true);

      logger.debug('[OnboardingContext] Reset successful');
    } catch (error) {
      logger.error('[OnboardingContext] Error resetting:', error);
      // Show onboarding anyway for better UX
      setShouldShowOnboarding(true);
    }
  }, [user]);

  const contextValue = useMemo(() => ({
    shouldShowOnboarding,
    isChecking,
    onboardingData,
    markOnboardingComplete,
    skipOnboarding,
    resetOnboarding,
    recheckOnboarding: checkOnboardingStatus,
  }), [shouldShowOnboarding, isChecking, onboardingData, markOnboardingComplete, skipOnboarding, resetOnboarding, checkOnboardingStatus]);

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}

export default OnboardingContext;
